import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../../api/endpoints';
import type { AreaElementType } from '../../api/types';
import { useDebounced } from '../../components/useDebounced';
import { elementTypeLabels } from '../../geo/labels';

export interface PickedElement {
  type: AreaElementType;
  reference_id?: string;
  code?: string;
}

interface Option {
  key: string;
  label: string;
  detail: string;
  element: PickedElement;
}

// ElementPicker sucht Landkreise, Gemeinden, PLZ oder Bundesländer und fügt sie einem Gebiet hinzu.
export function ElementPicker({ onPick, busy }: { onPick: (e: PickedElement) => void; busy: boolean }) {
  const [type, setType] = useState<AreaElementType>('COUNTY');
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const ready = type === 'POSTAL_CODE' ? /^\d{2,5}$/.test(q) : q.length >= 2 || type === 'FEDERAL_STATE';

  const results = useQuery({
    queryKey: ['geo', 'picker', type, q],
    enabled: ready,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }): Promise<Option[]> => {
      switch (type) {
        case 'COUNTY': {
          const res = await api.counties({ q }, signal);
          return res.items.map((c) => ({
            key: c.id,
            label: c.name,
            detail: `${c.type_label} · ${c.official_code}`,
            element: { type, reference_id: c.id },
          }));
        }
        case 'MUNICIPALITY': {
          const res = await api.municipalities({ q, limit: 20 }, signal);
          return res.items.map((m) => ({
            key: m.id,
            label: m.name,
            detail: `${m.county_name} · ${m.official_code}`,
            element: { type, reference_id: m.id },
          }));
        }
        case 'POSTAL_CODE': {
          const res = await api.postalCodes(q, signal);
          const byCode = new Map<string, string[]>();
          for (const p of res.items) byCode.set(p.postal_code, [...(byCode.get(p.postal_code) ?? []), p.city]);
          return [...byCode].map(([code, cities]) => ({
            key: code,
            label: code,
            detail: [...new Set(cities)].join(', '),
            element: { type, code },
          }));
        }
        case 'FEDERAL_STATE': {
          const res = await api.federalStates(signal);
          const lower = q.toLowerCase();
          return res.items
            .filter((s) => !lower || s.name.toLowerCase().includes(lower))
            .map((s) => ({ key: s.id, label: s.name, detail: s.code, element: { type, reference_id: s.id } }));
        }
      }
    },
  });

  return (
    <div className="element-picker">
      <div className="inline-form">
        <label className="field">
          <span>Art</span>
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value as AreaElementType);
              setQuery('');
            }}
          >
            {(['COUNTY', 'MUNICIPALITY', 'POSTAL_CODE', 'FEDERAL_STATE'] as AreaElementType[]).map((t) => (
              <option key={t} value={t}>
                {elementTypeLabels[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field grow">
          <span>Suche</span>
          <input
            value={query}
            inputMode={type === 'POSTAL_CODE' ? 'numeric' : 'text'}
            placeholder={type === 'POSTAL_CODE' ? 'PLZ oder PLZ-Anfang, z. B. 7017' : 'Name, z. B. Esslingen'}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      {ready && results.isSuccess && (
        <ul className="picker-results" aria-label="Suchergebnisse">
          {results.data.length === 0 && <li className="muted">Keine Treffer.</li>}
          {results.data.slice(0, 20).map((o) => (
            <li key={o.key}>
              <span>
                <strong>{o.label}</strong> <span className="muted">{o.detail}</span>
              </span>
              <button type="button" className="btn btn-small" disabled={busy} onClick={() => onPick(o.element)}>
                Hinzufügen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
